
import type { VideoApiActions } from './apiActions';
import type { SegmentUrlResolver } from './segmentUrlCache';
import type { StreamRoleId } from '@mimir/common/dto/mediaAvailability';
import type { ProgressFunc, SegmentFetcher } from '@mimir/common/format/mp4Subclip';
import { fetchAndMuxSubclipBlobs } from '@mimir/common/format/mp4Subclip';
import type { BlobView} from '@mimir/common/io/blobView';
import { BufferBlob, Offset } from '@mimir/common/io/blobView';
import type { Fraction } from '@mimir/common/util/fractionType';
import type { AxiosInstance, CancelTokenSource } from 'axios';
import Axios from 'axios';
import { ExponentialBackoff } from '@mimir/common/async/resilience';
import { allExceptionDetails } from '@mimir/common/util/exception';

export class CancelTokenManager {
  private allCts: Record<string, CancelTokenSource> = {};

  setUpSource(id: string) {
    const newSource = Axios.CancelToken.source();
    this.allCts[id] = newSource;
    return newSource;
  }

  cleanUpSource(id: string) {
    delete this.allCts[id];
  }

  cancelAll() {
    Object.keys(this.allCts).forEach(id => this.allCts[id]!.cancel(`Cancelling token id: ${id}`));
  }
}

class BrowserBlob implements BlobView {
  constructor(readonly blob: Blob, readonly start: Offset, readonly end: number) {
  }
  arrayBuffer(): Promise<ArrayBuffer> {
    return this.blobObject().arrayBuffer();
  }

  blobObject(): Blob {
    return this.blob.slice(this.start.position, this.end);
  }

  offset(): Offset {
    return this.start;
  }
  slice(start?: number, end?: number): BlobView {
    const newStart = this.start.add(start ?? 0);
    const relEnd = end ?? this.end;
    const absEnd = this.start.position + relEnd;
    return new BrowserBlob(this.blob, newStart, absEnd);
  }

  tryExtend(after: BlobView): BlobView | null {
    const other: BlobView & Partial<BrowserBlob> = after;
    if (
      this.blob == other.blob &&
      this.end == other.start?.position &&
      other.end != null
    ) {
      return new BrowserBlob(this.blob, this.start, other.end);
    }

    return null;
  }

  get size() {
    return this.end - this.start.position;
  }
}

export class AxiosSegmentFetcher implements SegmentFetcher {
  constructor(protected axios: AxiosInstance, protected segmentResolver: SegmentUrlResolver, protected itemId: string, protected cancelTokenManager: CancelTokenManager,
    protected streamRoleId: StreamRoleId) {
  }

  protected async cancellableFetch(url: string, index: number) {
    const ctsId = `${this.itemId}-${this.streamRoleId}-${index}`;
    let result;
    try {
      const cts = this.cancelTokenManager.setUpSource(ctsId);
      const cancelToken = cts.token;
      const backoff = new ExponentialBackoff({ timeLimit: 10 * 60 * 1000 });
      result = await backoff.resiliently(
        async () => {
          return this.axios.get(url, {
            responseType: 'arraybuffer',
            timeout: 3 * 60 * 1000,
            cancelToken,
          });
        },
        cancelToken,
        (ms, cause, failureCount) => {
          console.warn(`cancellableFetch for ${url} failed ${failureCount} times. retrying. `, JSON.stringify({ ...allExceptionDetails(cause), retryMs: Math.round(ms), failureCount, stack: undefined }));
        }
      );
    } finally {
      this.cancelTokenManager.cleanUpSource(ctsId);
    }

    return result;
  }

  async fetchSegmentTask(index: number): Promise<() => Promise<BlobView>> {
    const url = await this.segmentResolver.getMediaSegmentUrl(this.itemId, this.streamRoleId, index);
    return async () => {
      const arraybuffer = await this.cancellableFetch(url, index);
      return new BufferBlob(arraybuffer.data, new Offset(url, 0));
    };
  }

  close(): Promise<unknown> {
    return Promise.resolve();
  }
}

function toBrowserBlobPart(blobView: BlobView): Promise<BlobPart> {
  const bb: BlobView & Partial<BrowserBlob> = blobView;
  if (bb.blobObject) {
    return Promise.resolve(bb.blobObject());
  }

  return bb.arrayBuffer();
}

export async function prepareSubclipBlobs(api: VideoApiActions, segmentResolver: SegmentUrlResolver, itemId: string,
  segmentDuration: Fraction, firstSegmentIndex: number | null, lastSegmentIndex: number | null,
  prependSegmentZero: boolean, progress: ProgressFunc,
  streamRoleId: StreamRoleId, ctm?: CancelTokenManager): Promise<BlobView[] | null> {
  firstSegmentIndex = firstSegmentIndex ?? 1;
  if (lastSegmentIndex == null) {
    const item = await api.getPlayableItem(itemId, { useEditProxy: false, streamSetPreference: [[streamRoleId]] });
    if (!item || item.media == null) {
      return null;
    }
    lastSegmentIndex = item.media.segmentCount;
  }
  const fetcher = new AxiosSegmentFetcher(Axios, segmentResolver, itemId, ctm ?? new CancelTokenManager(), streamRoleId);
  return fetchAndMuxSubclipBlobs(fetcher, segmentDuration, firstSegmentIndex, lastSegmentIndex, prependSegmentZero, progress);
}

export async function prepareSubclipBrowserBlob(api: VideoApiActions, segmentResolver: SegmentUrlResolver,
  itemId: string, segmentDuration: Fraction, firstSegmentIndex: number | null, lastSegmentIndex: number | null,
  prependSegmentZero: boolean, progress: ProgressFunc,
  streamRoleId: StreamRoleId, ctm?: CancelTokenManager): Promise<Blob | null> {
  const blobs = await prepareSubclipBlobs(api, segmentResolver, itemId, segmentDuration, firstSegmentIndex, lastSegmentIndex, prependSegmentZero, progress, streamRoleId, ctm);
  if (blobs === null) {
    return null;
  }

  const blobParts = await Promise.all(blobs.map(toBrowserBlobPart));
  return new Blob(blobParts);
}
